import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Helper to get auth token
const getAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ==================== PUBLIC API ====================

export const getReviews = async () => {
  const response = await axios.get(`${API}/get_reviews`);
  return response.data;
};

export const getReviewsByType = async (reviewType) => {
  const response = await axios.get(`${API}/get_reviews_by_type`, {
    params: { review_type: reviewType }
  });
  return response.data;
};

export const addReview = async (reviewData) => {
  const response = await axios.post(`${API}/add_review`, reviewData);
  return response.data;
};

export const addClientRequest = async (requestData) => {
  const response = await axios.post(`${API}/add_client_request`, requestData);
  return response.data;
};

export const addContactMessage = async (messageData) => {
  const response = await axios.post(`${API}/add_contact_message`, messageData);
  return response.data;
};

export const getTeamMembers = async () => {
  const response = await axios.get(`${API}/get_team_members`);
  return response.data;
};

// ==================== AUTH API ====================

export const login = async (username, password) => {
  const response = await axios.post(`${API}/auth/login`, { username, password });
  return response.data;
};

export const googleLogin = async (credential) => {
  const response = await axios.post(`${API}/auth/google`, { credential });
  return response.data;
};

export const getAdminProfile = async () => {
  const response = await axios.get(`${API}/auth/profile`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateAdminProfile = async (displayName) => {
  const response = await axios.put(
    `${API}/auth/profile`,
    { display_name: displayName },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const updateAdminAvatar = async (file) => {
  const data = new FormData();
  data.append('avatar', file);

  const response = await axios.post(
    `${API}/auth/profile/avatar`,
    data,
    { headers: getAuthHeader() }
  );

  return response.data;
};

export const verifyToken = async () => {
  const response = await axios.get(`${API}/auth/verify_token`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const logout = async () => {
  const response = await axios.post(`${API}/auth/logout`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// ==================== ADMIN API ====================

export const adminGetReviews = async () => {
  const response = await axios.get(`${API}/admin/get_reviews`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminDeleteReview = async (reviewId) => {
  const response = await axios.delete(`${API}/admin/delete_review`, {
    params: { review_id: reviewId },
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminApproveReview = async (reviewId) => {
  const response = await axios.post(`${API}/admin/approve_review`, null, {
    params: { review_id: reviewId },
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminGetTopAgents = async () => {
  const response = await axios.get(`${API}/admin/get_top_agents`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminGetClients = async () => {
  const response = await axios.get(`${API}/admin/get_clients`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminUpdateClientStatus = async (clientId, statusData) => {
  const response = await axios.put(`${API}/admin/update_client_status`, statusData, {
    params: { client_id: clientId },
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminGetLogs = async () => {
  const response = await axios.get(`${API}/admin/get_logs`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminGetStats = async () => {
  const response = await axios.get(`${API}/admin/stats`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminGetSettings = async () => {
  const response = await axios.get(`${API}/get_settings`);
  return response.data;
};

export const adminGetContactMessages = async () => {
  const response = await axios.get(`${API}/admin/get_contact_messages`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminDeleteContactMessage = async (messageId) => {
  const response = await axios.delete(`${API}/admin/delete_contact_message`, {
    params: { message_id: messageId },
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminUpdateSettings = async (settingKey, settingValue) => {
  const response = await axios.post(`${API}/admin/update_settings`, null, {
    params: { setting_key: settingKey, setting_value: settingValue },
    headers: getAuthHeader()
  });
  return response.data;
};

// Before/After Images APIs
export const getBeforeAfterImages = async () => {
  const response = await axios.get(`${API}/get_before_after_images`);
  return response.data;
};

export const adminGetBeforeAfterImages = async () => {
  const response = await axios.get(`${API}/admin/get_before_after_images`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminAddBeforeAfterImage = async (beforeUrl, afterUrl, title, description, order) => {
  const response = await axios.post(`${API}/admin/add_before_after_image`, null, {
    params: {
      before_image_url: beforeUrl,
      after_image_url: afterUrl,
      title: title,
      description: description,
      display_order: order
    },
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminDeleteBeforeAfterImage = async (imageId) => {
  const response = await axios.delete(`${API}/admin/delete_before_after_image`, {
    params: { image_id: imageId },
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminUpdateBeforeAfterOrder = async (imageId, order) => {
  const response = await axios.put(`${API}/admin/update_before_after_order`, null, {
    params: { image_id: imageId, display_order: order },
    headers: getAuthHeader()
  });
  return response.data;
};

// Subscription Inquiry API
export const submitSubscriptionInquiry = async (data) => {
  const response = await axios.post(`${API}/add_subscription_inquiry`, data);
  return response.data;
};

// Admin - Get Subscription Inquiries
export const adminGetSubscriptionInquiries = async () => {
  const response = await axios.get(`${API}/admin/get_subscription_inquiries`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Admin - Delete Subscription Inquiry
export const adminDeleteSubscriptionInquiry = async (inquiryId) => {
  const response = await axios.delete(`${API}/admin/delete_subscription_inquiry`, {
    params: { inquiry_id: inquiryId },
    headers: getAuthHeader()
  });
  return response.data;
};

// Default export object
const api = {
  get: async (endpoint, config = {}) => {
    const response = await axios.get(`${API}${endpoint}`, {
      ...config,
      headers: { ...getAuthHeader(), ...config.headers }
    });
    return response;
  },
  post: async (endpoint, data, config = {}) => {
    const response = await axios.post(`${API}${endpoint}`, data, {
      ...config,
      headers: { ...getAuthHeader(), ...config.headers }
    });
    return response;
  },
  put: async (endpoint, data, config = {}) => {
    const response = await axios.put(`${API}${endpoint}`, data, {
      ...config,
      headers: { ...getAuthHeader(), ...config.headers }
    });
    return response;
  },
  delete: async (endpoint, config = {}) => {
    const response = await axios.delete(`${API}${endpoint}`, {
      ...config,
      headers: { ...getAuthHeader(), ...config.headers }
    });
    return response;
  }
};

export default api;


export const adminDeleteClient = async (clientId) => {
  const token = localStorage.getItem('admin_token');

  const response = await axios.delete(
    `${API_URL}/api/admin/delete_client/${clientId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  return response.data;
};
